import { useContext } from 'react';

import { Updater } from '@tanstack/react-table';
import isEqual from 'react-fast-compare';
import toast from 'react-hot-toast';

import { useMutation } from '@apollo/client';

import { AssetClassToLoanType } from '../app-level/config/assetClassTypes';
import {
  DataContext,
  DataContextActionType,
  GlobalTableOrderConfig,
  marketHomeTableColumnsOrder,
} from '../context/DataContext';
import { executedTradesColumnOrder } from '../features/deals/Executed/Table.config';
import { activeTradesAllTradesColumnOrder } from '../features/deals/InProgress/ActiveListingsTable.config';
import {
  autoColumnsOrder,
  creColumnsOrder,
  homeARMColumnsOrder,
  unsecpersonalColumnsOrder,
} from '../features/drilldown/LoanDatatable/loanTable.config';
import { listingColsOrder, ListingKey } from '../features/OnMarket/marketplace.config';
import { activeTradesBidsListingColsOrder } from '../features/pages/marketplace/ActiveTrades/bidsTable.config';
import { AddUserSetting } from '../mutation/__generated__/AddUserSetting';
import { ADD_USER_SETTING } from '../mutation/userData';

const excludedColumns = ['selection', 'icons'];

export const checkColumnCfgPayload = (userSettings: GlobalTableOrderConfig) => {
  const fallback = <T>(val: T | undefined, fallbackVal: T): T =>
    Array.isArray(val) && val.length ? val : fallbackVal;

  return {
    myPortfolio: {
      loanTable: {
        HOME: fallback(userSettings?.myPortfolio?.loanTable?.HOME, homeARMColumnsOrder),
        HOME_ARM: fallback(userSettings?.myPortfolio?.loanTable?.HOME_ARM, homeARMColumnsOrder),
        UNSECPERSONAL: fallback(
          userSettings?.myPortfolio?.loanTable?.UNSECPERSONAL,
          unsecpersonalColumnsOrder,
        ),
        AUTO: fallback(userSettings?.myPortfolio?.loanTable?.AUTO, autoColumnsOrder),
      },
    },
    marketPlace: {
      marketHomeTable: fallback(userSettings?.marketPlace?.marketHomeTable, [
        'view-deal',
        ...listingColsOrder,
      ]) as marketHomeTableColumnsOrder[],
      activeTrades: {
        allTradesTable: fallback(
          userSettings?.marketPlace?.activeTrades.allTradesTable,
          activeTradesAllTradesColumnOrder,
        ),
        bidsTable: fallback(
          userSettings?.marketPlace?.activeTrades.bidsTable,
          activeTradesBidsListingColsOrder,
        ),
      },

      executedTradesTable: fallback(
        userSettings?.marketPlace?.executedTradesTable,
        executedTradesColumnOrder,
      ),
    },
  };
};

export type AssetClassTableNames =
  | 'loanTable_HOME'
  | 'loanTable_HOME_ARM'
  | 'loanTable_AUTO'
  | 'loanTable_UNSECPERSONAL'
  | 'loanTable_CRE'
  | 'marketHomeTable'
  | 'allTradesTable'
  | 'bidsTable'
  | 'executedTradesTable';

export const useColumnOrder = (tableName: AssetClassTableNames, currentOrder: string[]) => {
  const { state, dispatch } = useContext(DataContext);

  const [addUserSetting] = useMutation<AddUserSetting>(ADD_USER_SETTING);

  const setColumnOrder = async (order: Updater<string[]>) => {
    const resolvedOrder = typeof order === 'function' ? order(currentOrder as string[]) : order;

    let payload;
    let resolvedCfgOrder = resolvedOrder;

    switch (tableName) {
      case 'loanTable_HOME':
        payload = {
          ...state.globalTableOrderConfig,
          myPortfolio: {
            loanTable: {
              ...state.globalTableOrderConfig.myPortfolio.loanTable,
              HOME: resolvedOrder.filter((item) => item !== 'selection')?.length
                ? resolvedOrder.filter((item) => item !== 'selection')
                : (homeARMColumnsOrder as (keyof AssetClassToLoanType['HOME'])[]),
            },
          },
        } as GlobalTableOrderConfig;

        resolvedCfgOrder = resolvedOrder.filter((item) => !excludedColumns.includes(item));
        break;

      case 'loanTable_HOME_ARM':
        payload = {
          ...state.globalTableOrderConfig,
          myPortfolio: {
            loanTable: {
              ...state.globalTableOrderConfig.myPortfolio.loanTable,
              HOME_ARM: resolvedOrder.filter((item) => item !== 'selection')?.length
                ? resolvedOrder.filter((item) => item !== 'selection')
                : (homeARMColumnsOrder as (keyof AssetClassToLoanType['HOME_ARM'])[]),
            },
          },
        } as GlobalTableOrderConfig;

        resolvedCfgOrder = resolvedOrder.filter((item) => !excludedColumns.includes(item));
        break;

      case 'loanTable_CRE':
        payload = {
          ...state.globalTableOrderConfig,
          myPortfolio: {
            loanTable: {
              ...state.globalTableOrderConfig.myPortfolio.loanTable,
              CRE: resolvedOrder.filter((item) => item !== 'selection')?.length
                ? resolvedOrder.filter((item) => item !== 'selection')
                : (creColumnsOrder as (keyof AssetClassToLoanType['CRE'])[]),
            },
          },
        } as GlobalTableOrderConfig;

        resolvedCfgOrder = resolvedOrder.filter((item) => !excludedColumns.includes(item));
        break;

      case 'loanTable_AUTO':
        payload = {
          ...state.globalTableOrderConfig,
          myPortfolio: {
            loanTable: {
              ...state.globalTableOrderConfig.myPortfolio.loanTable,
              AUTO: resolvedOrder.filter((item) => item !== 'selection')?.length
                ? resolvedOrder.filter((item) => item !== 'selection')
                : (autoColumnsOrder as (keyof AssetClassToLoanType['AUTO'])[]),
            },
          },
        } as GlobalTableOrderConfig;

        resolvedCfgOrder = resolvedOrder.filter((item) => !excludedColumns.includes(item));
        break;

      case 'loanTable_UNSECPERSONAL':
        payload = {
          ...state.globalTableOrderConfig,
          myPortfolio: {
            loanTable: {
              ...state.globalTableOrderConfig.myPortfolio.loanTable,
              UNSECPERSONAL: resolvedOrder.filter((item) => item !== 'selection')?.length
                ? resolvedOrder.filter((item) => item !== 'selection')
                : (unsecpersonalColumnsOrder as (keyof AssetClassToLoanType['UNSECPERSONAL'])[]),
            },
          },
        } as GlobalTableOrderConfig;

        resolvedCfgOrder = resolvedOrder.filter((item) => !excludedColumns.includes(item));
        break;

      case 'marketHomeTable':
        payload = {
          ...state.globalTableOrderConfig,
          marketPlace: {
            ...state.globalTableOrderConfig.marketPlace,
            marketHomeTable: resolvedOrder as ListingKey[],
          },
        } as GlobalTableOrderConfig;
        break;

      case 'allTradesTable':
        payload = {
          ...state.globalTableOrderConfig,
          marketPlace: {
            ...state.globalTableOrderConfig.marketPlace,
            activeTrades: {
              ...state.globalTableOrderConfig.marketPlace.activeTrades,
              allTradesTable: resolvedOrder as string[],
            },
          },
        } as GlobalTableOrderConfig;
        break;

      case 'bidsTable':
        payload = {
          ...state.globalTableOrderConfig,
          marketPlace: {
            ...state.globalTableOrderConfig.marketPlace,
            activeTrades: {
              ...state.globalTableOrderConfig.marketPlace.activeTrades,
              bidsTable: resolvedOrder as string[],
            },
          },
        } as GlobalTableOrderConfig;
        break;

      case 'executedTradesTable':
        payload = {
          ...state.globalTableOrderConfig,
          marketPlace: {
            ...state.globalTableOrderConfig.marketPlace,
            executedTradesTable: resolvedOrder as string[],
          },
        } as GlobalTableOrderConfig;
        break;

      default:
        return;
    }

    if (!isEqual(resolvedCfgOrder, currentOrder)) {
      dispatch({
        type: DataContextActionType.SET_GLOBAL_TABLE_ORDER_CONFIG,
        payload,
      });

      try {
        await addUserSetting({
          variables: {
            name: 'globalTableOrderConfig',
            value: JSON.stringify(payload),
          },
        });
      } catch {
        toast.error('Column order failed');
      }
    }
  };

  return {
    setColumnOrder,
  };
};
